import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, CheckCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { siteInfo } from '../data/mockData';

const FunerariaBairrosRJ = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // SEO Otimizado
  useEffect(() => {
    document.title = 'Funerária no Meier, Tijuca, Centro, Barra e Botafogo | RJ 24h';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Funerária completa no Meier, Tijuca, Centro, Barra da Tijuca e Botafogo. Atendimento 24h na Zona Norte e Zona Sul. Translado, velório e sepultamento.';
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const bairros = [
    {
      nome: 'Meier',
      zona: 'Zona Norte',
      descricao: 'Atendimento funerário 24 horas no Meier e região. Remoção imediata.',
      link: '/funeraria-no-meier'
    },
    {
      nome: 'Tijuca',
      zona: 'Zona Norte',
      descricao: 'Funerária na Tijuca com serviços completos de velório e sepultamento.',
      link: '/funeraria-na-tijuca'
    },
    {
      nome: 'Centro',
      zona: 'Centro',
      descricao: 'Atendimento funerário no Centro do Rio com equipe especializada.',
      link: '/funeraria-no-centro'
    },
    {
      nome: 'Barra da Tijuca',
      zona: 'Zona Oeste',
      descricao: 'Funerária na Barra da Tijuca. Atendimento rápido na Zona Oeste.',
      link: '/funeraria-barra-da-tijuca'
    },
    {
      nome: 'Botafogo',
      zona: 'Zona Sul',
      descricao: 'Serviço funerário completo em Botafogo e toda a Zona Sul do RJ.',
      link: '/funeraria-em-botafogo'
    }
  ];

  const servicos = [
    {
      titulo: 'Translado Funerário',
      descricao: 'Remoção e transporte funerário para qualquer região do Rio de Janeiro',
      icon: '🚗'
    },
    {
      titulo: 'Velório Completo',
      descricao: 'Organização de velório com estrutura completa e ornamentação',
      icon: '🕯️'
    },
    {
      titulo: 'Sepultamento',
      descricao: 'Assistência para sepultamento em cemitérios públicos e particulares',
      icon: '🪦'
    },
    {
      titulo: 'Cremação',
      descricao: 'Serviço de cremação em crematórios certificados do Rio de Janeiro',
      icon: '🔥'
    },
    {
      titulo: 'Documentação',
      descricao: 'Orientação completa sobre documentação funerária necessária',
      icon: '📋'
    },
    {
      titulo: 'Atendimento 24h',
      descricao: 'Equipe disponível 24 horas por dia, 7 dias por semana',
      icon: '⏰'
    }
  ];

  const faqItems = [
    {
      question: 'Quais bairros do Rio de Janeiro vocês atendem?',
      answer: 'Atendemos todo o Rio de Janeiro, com especial presença no Meier, Tijuca, Centro, Barra da Tijuca, Botafogo e todas as regiões da Zona Norte, Zona Sul, Centro e Zona Oeste. Nossa equipe está disponível 24 horas para atender qualquer localidade.'
    },
    {
      question: 'Quanto tempo demora para chegar no local?',
      answer: 'Nosso tempo de resposta é imediato. Dependendo do bairro e da disponibilidade da equipe, chegamos ao local em 30 a 60 minutos após o primeiro contato. Meier, Tijuca e Centro têm atendimento ainda mais rápido.'
    },
    {
      question: 'Atendem tanto Zona Norte quanto Zona Sul?',
      answer: 'Sim! Atendemos toda a Zona Norte (Meier, Tijuca, Jacarepaguá), Zona Sul (Botafogo, Copacabana, Ipanema), Centro e Zona Oeste (Barra da Tijuca, Recreio). Nosso serviço funerário cobre todo o município do Rio de Janeiro.'
    },
    {
      question: 'Fazem translado entre diferentes bairros?',
      answer: 'Sim, fazemos translado funerário entre quaisquer bairros do Rio de Janeiro e também para outros estados. Se o falecimento ocorreu no Meier e o sepultamento será na Barra, por exemplo, cuidamos de todo o transporte.'
    },
    {
      question: 'Qual o valor do serviço funerário no Rio de Janeiro?',
      answer: 'Os valores variam de R$ 1.480 (funeral simples) até R$ 2.890 (funeral completo), dependendo dos serviços contratados. Entre em contato para receber um orçamento detalhado para o seu bairro.'
    },
    {
      question: 'Vocês trabalham com quais cemitérios do Rio?',
      answer: 'Trabalhamos com cemitérios públicos e particulares em toda a região: Cemitério do Caju, São João Batista, Catumbi, Jardim da Saudade, Sulacap, entre outros. Orientamos a família sobre as melhores opções próximas ao bairro.'
    },
    {
      question: 'Como funciona o velório em cada bairro?',
      answer: 'Oferecemos velório completo com sala climatizada, ornamentação e recepção em diversos cemitérios e capelas do Rio de Janeiro. A localização é escolhida conforme a preferência da família e proximidade do bairro.'
    },
    {
      question: 'Posso solicitar atendimento pelo WhatsApp?',
      answer: 'Sim! O WhatsApp é uma das formas mais rápidas de contato. Ligue (21) 4106-4022 ou envie mensagem pelo WhatsApp para receber atendimento imediato, independentemente do bairro onde você está.'
    }
  ];

  // Schema.org
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "FuneralHome",
    "name": "Santa Casa Assistencial - Funerária Zona Norte e Zona Sul RJ",
    "description": "Funerária completa no Meier, Tijuca, Centro, Barra da Tijuca e Botafogo. Atendimento 24 horas na Zona Norte e Zona Sul do Rio de Janeiro.",
    "url": "https://funerariasantacasaassistencial.com/funeraria-zona-norte-zona-sul",
    "telephone": "+55-21-4106-4022",
    "priceRange": "R$ 1.480 - R$ 2.890",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Rio de Janeiro",
      "addressRegion": "RJ",
      "addressCountry": "BR"
    },
    "areaServed": [
      { "@type": "City", "name": "Meier" },
      { "@type": "City", "name": "Tijuca" },
      { "@type": "City", "name": "Centro" },
      { "@type": "City", "name": "Barra da Tijuca" },
      { "@type": "City", "name": "Botafogo" },
      { "@type": "City", "name": "Zona Norte Rio de Janeiro" },
      { "@type": "City", "name": "Zona Sul Rio de Janeiro" }
    ],
    "sameAs": [
      "https://wa.me/552141064022",
      "https://www.instagram.com/santacasa.assistencial"
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-teal-700 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <MapPin className="w-8 h-8 mr-3" />
              <span className="text-lg font-semibold">Atendemos Toda a Região</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
              Funerária Completa no Meier, Tijuca, Centro, Barra e Botafogo
            </h1>
            <h2 className="text-xl md:text-2xl mb-6 text-teal-50 font-light">
              Translado | Velório | Sepultamento | Atendimento Funerário Zona Norte e Zona Sul
            </h2>
            <p className="text-base md:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
              A Santa Casa Assistencial oferece <strong>atendimento funerário 24 horas</strong> em todos os principais bairros do Rio de Janeiro. 
              Equipe especializada para remoção, velório, sepultamento, cremação e translado.
            </p>
            <div className="flex items-center justify-center space-x-2 mb-8">
              <Clock className="w-5 h-5" />
              <span className="text-xl font-bold">Atendimento 24 Horas • Toda Semana</span>
            </div>

            {/* CTAs Principais */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:41064022"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'phone_click',
                    click_location: 'hero_bairros_rj'
                  });
                }}
                className="inline-flex items-center justify-center bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <Phone className="w-5 h-5 mr-2" />
                LIGAR AGORA
              </a>
              <a
                href="https://wa.me/552141064022?text=Olá,%20preciso%20de%20atendimento%20funerário"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'whatsapp_click',
                    click_location: 'hero_bairros_rj'
                  });
                }}
                className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WHATSAPP 24 HORAS
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bairros Atendidos */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Bairros Atendidos no Rio de Janeiro
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center max-w-3xl mx-auto">
            Cobertura completa na <strong>Zona Norte</strong>, <strong>Zona Sul</strong>, <strong>Centro</strong> e <strong>Zona Oeste</strong> do Rio de Janeiro. 
            Atendimento imediato em qualquer localidade.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {bairros.map((bairro, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all border-l-4 border-teal-500">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-2xl font-bold text-gray-900">{bairro.nome}</h3>
                  <span className="text-xs bg-teal-100 text-teal-700 px-3 py-1 rounded-full font-semibold">
                    {bairro.zona}
                  </span>
                </div>
                <p className="text-gray-700 mb-4">{bairro.descricao}</p>
                <Link
                  to={bairro.link}
                  className="inline-block text-teal-600 hover:text-teal-700 font-semibold transition-colors"
                >
                  Ver página específica →
                </Link>
              </div>
            ))}

            {/* Card Outros Bairros */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-xl shadow-lg p-6">
              <h3 className="text-2xl font-bold mb-3">+ Outros Bairros</h3>
              <p className="mb-4">
                Também atendemos: Copacabana, Ipanema, Leblon, Jacarepaguá, Recreio, Maracanã, Vila Isabel, e toda a região metropolitana.
              </p>
              <a
                href="https://wa.me/552141064022?text=Gostaria%20de%20confirmar%20atendimento%20no%20meu%20bairro"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-teal-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Consultar Meu Bairro
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Serviços */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Serviços Funerários Completos
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center">
            Atendimento completo em <strong>translado</strong>, <strong>velório</strong> e <strong>sepultamento</strong> para todos os bairros do RJ.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {servicos.map((servico, index) => (
              <div key={index} className="bg-teal-50 rounded-xl p-6 text-center">
                <div className="text-5xl mb-4">{servico.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{servico.titulo}</h3>
                <p className="text-gray-700">{servico.descricao}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Gostaria%20de%20saber%20mais%20sobre%20os%20serviços"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'servicos_bairros',
                  action: 'consultar_servicos'
                });
              }}
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              SOLICITAR ATENDIMENTO 24 HORAS
            </a>
          </div>
        </div>
      </section>

      {/* Valores e Preços */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Valores Transparentes para Todos os Bairros
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center">
            Nossos valores são os mesmos para <strong>Meier</strong>, <strong>Tijuca</strong>, <strong>Centro</strong>, 
            <strong> Barra</strong> e <strong>Botafogo</strong>. Sem taxas extras por região.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Funeral Simples */}
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-2 border-teal-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Funeral Simples</h3>
              <p className="text-4xl font-bold text-teal-600 mb-4">R$ 1.480<span className="text-lg">*</span></p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Remoção e transporte</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Urna funerária</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Preparação básica</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Documentação</span>
                </li>
              </ul>
              <Link
                to="/funeral"
                className="block w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-lg font-bold transition-all"
              >
                Ver Detalhes
              </Link>
            </div>

            {/* Funeral Completo */}
            <div className="bg-white rounded-xl shadow-2xl p-6 text-center border-4 border-teal-500 relative transform md:scale-105">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-red-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                  MAIS CONTRATADO
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-2">Funeral Completo</h3>
              <p className="text-4xl font-bold text-teal-600 mb-4">R$ 2.890<span className="text-lg">*</span></p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Tudo do Simples +</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Tanatopraxia</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Necromaquiagem</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Coroa de flores</span>
                </li>
              </ul>
              <Link
                to="/funeral"
                className="block w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-lg font-bold transition-all"
              >
                Ver Detalhes
              </Link>
            </div>

            {/* Cremação */}
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-2 border-orange-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cremação</h3>
              <p className="text-4xl font-bold text-orange-600 mb-4">R$ 2.490<span className="text-lg">*</span></p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Remoção completa</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Taxa de cremação</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Urna com cinzas</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Documentação</span>
                </li>
              </ul>
              <Link
                to="/cremacao"
                className="block w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-bold transition-all"
              >
                Ver Detalhes
              </Link>
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center mb-8">
            *Valores válidos para todos os bairros atendidos. Consulte condições completas com nossa equipe.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Perguntas Frequentes - Atendimento por Bairro
          </h2>
          <div className="space-y-4">
            {faqItems.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Precisa de Atendimento Funerário Agora?
          </h2>
          <p className="text-xl mb-4">
            Atendemos <strong>Meier, Tijuca, Centro, Barra da Tijuca e Botafogo</strong>
          </p>
          <p className="text-lg mb-8 text-teal-50">
            Zona Norte • Zona Sul • Centro • Zona Oeste • Toda a Região Metropolitana
          </p>
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Clock className="w-6 h-6" />
            <span className="text-2xl font-bold">ATENDIMENTO 24 HORAS</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:41064022"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'phone_click',
                  click_location: 'cta_final_bairros'
                });
              }}
              className="inline-flex items-center justify-center bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              LIGAR AGORA: 4106-4022
            </a>
            <a
              href="https://wa.me/552141064022?text=Preciso%20de%20atendimento%20funerário%20urgente"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'cta_final_bairros',
                  urgency: 'high'
                });
              }}
              className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WHATSAPP 24 HORAS
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FunerariaBairrosRJ;
